import { apiClient } from './client';
import type {
  TagCategory,
  TagCategoryWithValues,
  TagValue,
  CreateTagCategoryDto,
  CreateTagValueDto
} from '@/types/tag.types';

export class TagCategoryService {

  /**
   * Get all accessible tag categories with values
   */
  async getCategoriesWithValues(): Promise<TagCategoryWithValues[]> {
    const response = await apiClient.get('/tag-categories');
    return response.data.data || [];
  }

  /**
   * Get all accessible tag categories (without values)
   */
  async getCategories(): Promise<TagCategory[]> {
    const response = await apiClient.get('/tag-categories?include_values=false');
    return response.data.data || [];
  }

  /**
   * Create a custom tag category
   */
  async createCategory(data: CreateTagCategoryDto): Promise<TagCategory> {
    const response = await apiClient.post('/tag-categories', data);
    return response.data.data;
  }

  /**
   * Update a custom tag category
   */
  async updateCategory(id: number, data: Partial<CreateTagCategoryDto>): Promise<TagCategory> {
    const response = await apiClient.put(`/tag-categories/${id}`, data);
    return response.data.data;
  }

  /**
   * Delete a custom tag category
   */
  async deleteCategory(id: number): Promise<void> {
    await apiClient.delete(`/tag-categories/${id}`);
  }

  /**
   * Get values for a category
   */
  async getValues(categoryId: number): Promise<TagValue[]> {
    const response = await apiClient.get(`/tag-categories/${categoryId}/values`);
    return response.data.data || [];
  }

  /**
   * Add a value to a category
   */
  async addValue(categoryId: number, data: CreateTagValueDto): Promise<TagValue> {
    const response = await apiClient.post(`/tag-categories/${categoryId}/values`, data);
    return response.data.data;
  }

  /**
   * Delete a custom tag value
   */
  async deleteValue(valueId: number): Promise<void> {
    await apiClient.delete(`/tag-categories/values/${valueId}`);
  }

  /**
   * Search tag values by prefix
   */
  async searchValues(query: string, limit: number = 20): Promise<TagValue[]> {
    const response = await apiClient.get(`/tag-categories/search?q=${encodeURIComponent(query)}&limit=${limit}`);
    return response.data.data || [];
  }
}

export const tagCategoryService = new TagCategoryService();
